import React, { useEffect, useRef, useState } from 'react';

import { generate } from '@pdfme/generator';
import {
  barcodes,
  checkbox,
  dateTime,
  ellipse,
  image,
  line,
  radioGroup,
  rectangle,
  select,
  table,
  text,
  time,
} from '@pdfme/schemas';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Loader,
  LoadingButton,
  MenuItem,
  PageHeader,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';
import { Printer } from 'iconsax-react';

import {
  Template,
  getInputFromTemplate,
  useSelector,
  useTranslation,
} from 'third-party';

import { DEFAULT_TIMEZONE } from 'constants/common';

import { useBank } from 'modules/bank';
import { useBlankCheck } from 'modules/blankCheck';
import { useSettings } from 'modules/setting';

import {
  bankSelectors,
  blankCheckSelectors,
  settingSelectors,
} from 'selectors';

import { font } from 'features/CheckDesigner/CheckDesigner';
import { getSampleCheckTemplate } from 'features/CheckDesigner/sampleData';

import { formatDateTimeWithTimezone } from 'utils/app';
import { removeNullFields } from 'utils/helper';
import { isValidCheckNumber, sanitizeCheckNumber } from 'utils/payment';

import { APIBlankCheck, PrintType } from 'types/api';
import { APISettingName } from 'types/backend';

const plugins = {
  text,
  image,
  qrcode: barcodes.qrcode,
  line,
  table,
  rectangle,
  ellipse,
  dateTime,
  time,
  select,
  checkbox,
  radioGroup,
};

const SIGNATURE_FIELDS = ['signature', 'signatureImage', 'signatureField'];

type Props = {
  bankId: string;
  onClose: () => void;
};

export const BlankCheckPrintModal: React.FC<Props> = ({ bankId, onClose }) => {
  const { t } = useTranslation();
  const { error: showError, success } = useNotifications();
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );
  const bank = useSelector(bankSelectors.bank);
  const prints = useSelector(blankCheckSelectors.prints);
  const printsPage = useSelector(blankCheckSelectors.printsPage);
  const allPrintsLoaded = useSelector(blankCheckSelectors.allPrintsLoaded);
  const generatePending = useSelector(blankCheckSelectors.generatePending);
  const loadMorePrintsPending = useSelector(
    blankCheckSelectors.loadMorePrintsPending,
  );

  const [checkNumberFrom, setCheckNumberFrom] = useState('');
  const [checkNumberTo, setCheckNumberTo] = useState('');
  const [withSignature, setWithSignature] = useState(true);
  const [printType, setPrintType] = useState<PrintType>(PrintType.Check);
  const [template, setTemplate] = useState<Template | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const {
    logBlankCheck,
    loadBlankChecks,
    loadMoreBlankChecks,
    clearBlankChecks,
  } = useBlankCheck();
  const { loadCheckTemplate } = useSettings();
  const { loadBank, clearBank } = useBank();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const printingRef = useRef(false);

  useEffect(() => {
    loadBank(bankId);
    loadBlankChecks(bankId);
    return () => {
      clearBank();
      clearBlankChecks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId]);

  useEffect(() => {
    setLoadingTemplate(true);
    loadCheckTemplate(bankId, printType).then((result: any) => {
      setLoadingTemplate(false);
      if (result?.payload) {
        setTemplate(removeNullFields(result.payload) as Template);
      } else {
        setTemplate(getSampleCheckTemplate());
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, printType]);

  // Infinite scroll: observe sentinel at bottom of history list
  useEffect(() => {
    if (!sentinelRef.current || allPrintsLoaded || prints.length === 0) return;

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || loadMorePrintsPending || allPrintsLoaded)
            continue;
          loadMoreBlankChecks(bankId, printsPage + 1);
        }
      },
      {
        root: root && root.scrollHeight > root.clientHeight + 10 ? root : null,
        threshold: 0,
      },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [allPrintsLoaded, loadMorePrintsPending, prints.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const buildBankData = (): Record<string, string> => {
    if (!bank) return {};
    const address = `${bank.address}\n${bank.city}, ${bank.state} ${bank.zip}`;
    const bankInfo = `⑆${bank.routing}⑆  ${bank.account}⑆  001001`;
    return {
      bankName: bank.bankName,
      companyName: bank.companyName,
      companyAddress: address,
      compnayAddressMiddle: address,
      bankInfo,
    };
  };

  const buildInputForCheckNumber = (
    checkNumber: string,
  ): Record<string, string> => {
    if (!template) return {};
    const base = getInputFromTemplate(template)[0] ?? {};
    const result: Record<string, string> = {};

    Object.entries(base).forEach(([key, value]) => {
      result[key] =
        typeof value === 'string' && /^\{\{.+\}\}$/.test(value) ? '' : value;
    });

    Object.assign(result, buildBankData());
    result['checkNumber'] = checkNumber;
    result['checkNumberLower'] = checkNumber;

    if (!withSignature) {
      SIGNATURE_FIELDS.forEach(field => {
        result[field] = '';
      });
    }

    return result;
  };

  const long = (s: string): bigint | null => {
    try {
      return BigInt(s);
    } catch {
      return null;
    }
  };

  const getCheckNumbers = (): string[] | null => {
    const from = checkNumberFrom.trim();
    const to = checkNumberTo.trim();
    if (!from || !to) return null;
    if (!isValidCheckNumber(from) || !isValidCheckNumber(to)) return null;

    const f = long(from)!;
    const t = long(to)!;
    if (f > t) return null;
    const padLen = from.length;
    return Array.from({ length: Number(t - f) + 1 }, (_, i) =>
      String(f + BigInt(i)).padStart(padLen, '0'),
    );
  };

  // const handlePreview = async () => {
  //   if (!template) return;
  //   const numbers = getCheckNumbers();
  //   if (!numbers?.length) {
  //     showError(t('blankCheck.errors.invalidRange'));
  //     return;
  //   }
  //   try {
  //     const pdf = await generate({
  //       template,
  //       plugins,
  //       inputs: numbers.map(buildInputForCheckNumber),
  //       options: { font },
  //     });
  //     window.open(
  //       URL.createObjectURL(
  //         new Blob([pdf as any], { type: 'application/pdf' }),
  //       ),
  //     );
  //   } catch (e: any) {
  //     showError(e?.message ?? t('blankCheck.errors.generateFailed'));
  //   }
  // };

  const handlePrint = async () => {
    if (printingRef.current || !template) return;
    const numbers = getCheckNumbers();
    if (!numbers?.length) {
      showError(t('blankCheck.errors.invalidRange'));
      return;
    }

    printingRef.current = true;
    setIsPrinting(true);
    try {
      const result = await logBlankCheck(bankId, {
        checkNumberFrom: checkNumberFrom.trim(),
        checkNumberTo: checkNumberTo.trim(),
        withSignature,
        printType,
      });

      if ((result as any).error) {
        const payload = (result as any).payload;
        showError(
          payload?.body?.title ||
            (payload?.status === 400
              ? t('blankCheck.errors.overlappingRange')
              : t('blankCheck.errors.generateFailed')),
        );
        return;
      }

      const pdf = await generate({
        template,
        plugins,
        inputs: numbers.map(buildInputForCheckNumber),
        options: { font },
      });
      const url = URL.createObjectURL(
        new Blob([pdf as any], { type: 'application/pdf' }),
      );
      const win = window.open(url);
      if (win) {
        win.onload = () => {
          win.focus();
          win.print();
        };
      }
      success(t('blankCheck.success.generated'));
    } catch (e: any) {
      showError(e?.message ?? t('blankCheck.errors.generateFailed'));
    } finally {
      printingRef.current = false;
      setIsPrinting(false);
    }
  };

  const fromError =
    checkNumberFrom.trim() !== '' && !isValidCheckNumber(checkNumberFrom);
  const toError =
    checkNumberTo.trim() !== '' && !isValidCheckNumber(checkNumberTo);

  const checkNumbers = getCheckNumbers();
  const checkCount = checkNumbers?.length ?? 0;
  const isRangeValid = checkCount > 0 && !fromError && !toError;
  const isLoading = loadingTemplate || !bank;

  return (
    <Box padding="1.5rem">
      <PageHeader
        header={t('blankCheck.title')}
        subHeader={t('blankCheck.subTitle')}
      >
        <Box display="flex" columnGap="1rem">
          <LoadingButton
            variant="outlined"
            size="medium"
            startIcon={<Printer variant="TwoTone" size="24" />}
            disabled={!isRangeValid || !template || isLoading || isPrinting}
            loading={generatePending || isPrinting}
            onClick={handlePrint}
            color="primary"
          >
            {t('blankCheck.actions.printAll')}
          </LoadingButton>
          <Button
            variant="outlined"
            size="medium"
            onClick={onClose}
            color="error"
          >
            {t('common.form.cancel')}
          </Button>
        </Box>
      </PageHeader>

      {isLoading ? (
        <Loader />
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              {t('blankCheck.bankInfo')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {bank.bankName} — {bank.accountName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('blankCheck.routing')}: {bank.routing} &nbsp;|&nbsp;
              {t('blankCheck.account')}: {bank.account}
            </Typography>
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel id="print-type-label">
              {t('blankCheck.printType')}
            </InputLabel>
            <Select
              labelId="print-type-label"
              value={printType}
              label={t('blankCheck.printType')}
              onChange={e => setPrintType(e.target.value as PrintType)}
            >
              <MenuItem value={PrintType.Check}>
                {t('blankCheck.checkPaper')}
              </MenuItem>
              <MenuItem value={PrintType.WhitePaper}>
                {t('blankCheck.whitePaper')}
              </MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1.5}>
            <TextField
              label={t('blankCheck.checkNumberFrom')}
              size="small"
              fullWidth
              value={checkNumberFrom}
              onChange={e =>
                setCheckNumberFrom(sanitizeCheckNumber(e.target.value))
              }
              inputProps={{ inputMode: 'numeric', maxLength: 100 }}
              error={fromError}
              helperText={
                fromError
                  ? t('blankCheck.errors.invalidCheckNumber')
                  : undefined
              }
            />
            <TextField
              label={t('blankCheck.checkNumberTo')}
              size="small"
              fullWidth
              value={checkNumberTo}
              onChange={e =>
                setCheckNumberTo(sanitizeCheckNumber(e.target.value))
              }
              inputProps={{ inputMode: 'numeric', maxLength: 100 }}
              error={toError}
              helperText={
                toError ? t('blankCheck.errors.invalidCheckNumber') : undefined
              }
            />
          </Stack>

          {isRangeValid && (
            <Typography variant="caption" color="text.secondary">
              {t('blankCheck.checkCount', { count: checkCount })}
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={withSignature}
                  onChange={e => setWithSignature(e.target.checked)}
                />
              }
              label={
                withSignature
                  ? t('blankCheck.withSignature')
                  : t('blankCheck.withoutSignature')
              }
            />
            {/* <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOutlined />}
              disabled={!isRangeValid || !template}
              onClick={handlePreview}
            >
              {t('blankCheck.actions.preview')}
            </Button> */}
          </Box>

          {prints.length > 0 && (
            <>
              <Divider />
              <Typography variant="caption" color="text.secondary">
                {t('blankCheck.history.title')}
              </Typography>
              <Box
                ref={scrollContainerRef}
                sx={{ maxHeight: '11rem', overflowY: 'auto' }}
              >
                <Stack spacing={1}>
                  {prints.map((p: APIBlankCheck) => (
                    <Box
                      key={p.id}
                      sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                    >
                      <Typography variant="caption">
                        {p.checkNumberFrom} – {p.checkNumberTo}
                        &nbsp;|&nbsp;
                        {p.withSignature
                          ? t('blankCheck.withSignature')
                          : t('blankCheck.withoutSignature')}
                        &nbsp;|&nbsp;
                        {formatDateTimeWithTimezone(p.createdOn, timezoneValue)}
                        {p.createdBy &&
                          ` — ${p.createdBy.firstName} ${p.createdBy.lastName}`}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                {loadMorePrintsPending && <Loader />}
                <Box ref={sentinelRef} sx={{ height: 1 }} />
              </Box>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};
